import { chakra } from '@chakra-ui/react';
import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';
import { focusRing } from '../primitives/_shared';

const Anchor = chakra('div', { base: { position: 'relative', display: 'inline-flex' } });

const Panel = chakra('div', {
  base: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    minWidth: '16rem',
    bg: 'surface.raised',
    color: 'text.primary',
    borderRadius: 'lg',
    borderWidth: '1px',
    borderColor: 'border.default',
    boxShadow: 'lg',
    py: '1',
    zIndex: 'dropdown',
    overflow: 'hidden',
  },
});

export interface MenuProps {
  open: boolean;
  onClose: () => void;
  /** The already-rendered trigger button (must toggle `open` itself). */
  trigger: ReactNode;
  /** Accessible label for the menu surface. */
  label: string;
  /** Menu contents (use {@link MenuItem} or arbitrary nodes). */
  children: ReactNode;
  align?: 'start' | 'end';
  width?: string;
  'data-testid'?: string;
}

/**
 * A lightweight anchored dropdown: click-outside and Escape close it, focus moves into
 * the panel on open and returns to the trigger on close. Positioning is simple
 * (below the trigger, edge-aligned) — enough for top-bar menus like the notifications
 * bell without pulling in a positioning engine.
 */
export function Menu({
  open,
  onClose,
  trigger,
  label,
  children,
  align = 'end',
  width,
  ...rest
}: MenuProps) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const close = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (!anchorRef.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    // Move focus into the panel for keyboard users.
    const id = requestAnimationFrame(() => {
      const first = panelRef.current?.querySelector<HTMLElement>(
        'a,button,[tabindex]:not([tabindex="-1"])',
      );
      first?.focus();
    });
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
      cancelAnimationFrame(id);
    };
  }, [open, close]);

  return (
    <Anchor ref={anchorRef}>
      {trigger}
      {open && (
        <Panel
          ref={panelRef}
          role="menu"
          id={menuId}
          aria-label={label}
          width={width}
          {...(align === 'end' ? { right: 0 } : { left: 0 })}
          {...rest}
        >
          {children}
        </Panel>
      )}
    </Anchor>
  );
}

const ItemBase = chakra('button', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '0.5',
    width: '100%',
    textAlign: 'left',
    px: '3.5',
    py: '2.5',
    bg: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'text.primary',
    fontFamily: 'body',
    fontSize: 'md',
    _hover: { bg: 'action.secondary' },
    '&[data-unread="true"]': { bg: 'action.primary/8' },
    _disabled: { opacity: 0.6, cursor: 'default', _hover: { bg: 'transparent' } },
    ...focusRing,
  },
});

export interface MenuItemProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  children: ReactNode;
}

/** A selectable row inside a {@link Menu}. */
export function MenuItem({ children, type = 'button', ...rest }: MenuItemProps) {
  return (
    <ItemBase role="menuitem" type={type} {...rest}>
      {children}
    </ItemBase>
  );
}
